// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import DPSLTPControlTemplate from '@front/templates/DPSLTPControlTemplate.html';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { DynPropertyControl } from '../elements/DynPropertyControl';
import { ControlsTypes } from '../UtilsClasses/FactoryConstants';
export class DPSLTPControl extends DynPropertyControl {
    constructor() {
        super();
    }
    getType() {
        return ControlsTypes.DPSLTPControl;
    }
    oninit() {
        super.oninit();
        this.observe('dp.sl.inOffset dp.sl.offsetType dp.tp.inOffset dp.tp.offsetType dp.trailingStop.enabled', this.localize);
    }
    oncomplete() {
        super.oncomplete();
        void this.set('SLNumericLink', this.Controls.SLNumeric);
        void this.set('TPNumericLink', this.Controls.TPNumeric);
    }
    localize() {
        super.localize();
        const isSlInOffset = this.get('dp.sl.inOffset');
        const slOffsetType = this.get('dp.sl.offsetType');
        const isTrailingStop = this.get('dp.trailingStop.enabled');
        const trStopOffsetType = this.get('dp.trailingStop.offsetType');
        const isTpInOffset = this.get('dp.tp.inOffset');
        const tpOffsetType = this.get('dp.tp.offsetType');
        const slLocKey = isSlInOffset || isTrailingStop
            ? 'panel.newOrderEntry.stopLossCheckBox.offset'
            : 'panel.newOrderEntry.stopLossCheckBox';
        const sllLocKey = isSlInOffset
            ? 'panel.newOrderEntry.stopLimitLimitOffset'
            : 'panel.newOrderEntry.stopLimitLimitPrice';
        const tpLocKey = isTpInOffset
            ? 'panel.newOrderEntry.takeProfitCheckBox.offset'
            : 'panel.newOrderEntry.takeProfitCheckBox';
        const slLocKeyOffset = isSlInOffset || isTrailingStop
            ? 'panel.newOrderEntry.stopLossCheckBox'
            : 'panel.newOrderEntry.stopLossCheckBox.offset';
        const tpLocKeyOffset = isTpInOffset
            ? 'panel.newOrderEntry.takeProfitCheckBox'
            : 'panel.newOrderEntry.takeProfitCheckBox.offset';
        const slTooltipKey = isSlInOffset || isTrailingStop
            ? 'panel.newOrderEntry.stopLossNumeric.ToolTip.offset'
            : 'panel.newOrderEntry.stopLossNumeric.ToolTip';
        const tpTooltipKey = isTpInOffset
            ? 'panel.newOrderEntry.takeProfitNumeric.ToolTip.offset'
            : 'panel.newOrderEntry.takeProfitNumeric.ToolTip';
        const slNumericRightAlignedText = isSlInOffset
            ? SlTpUtils.getSymbolForOffsetMode(isTrailingStop ? trStopOffsetType : slOffsetType)
            : '';
        const tpNumericRightAlignedText = isTpInOffset ? SlTpUtils.getSymbolForOffsetMode(tpOffsetType) : '';
        void this.set({
            slLabel: Resources.getResource(slLocKey),
            slNumericRightAlignedText,
            sllLabel: Resources.getResource(sllLocKey),
            sllLabelTooltip: Resources.getResource(sllLocKey + '.tt'),
            tpLabel: Resources.getResource(tpLocKey),
            tpNumericRightAlignedText,
            slLabelOffset: Resources.getResource(slLocKeyOffset),
            tpLabelOffset: Resources.getResource(tpLocKeyOffset),
            slTooltip: Resources.getResource(slTooltipKey),
            tpTooltip: Resources.getResource(tpTooltipKey)
        });
    }
}
DynPropertyControl.extendWith(DPSLTPControl, {
    template: DPSLTPControlTemplate,
    data: function () {
        return {
            instrument: null,
            slLabel: '',
            slNumericRightAlignedText: '',
            tpLabel: '',
            tpNumericRightAlignedText: '',
            slLabelOffset: '',
            sllLabel: '', // #91413
            sllLabelTooltip: '', // #91413
            tpLabelOffset: '',
            slTooltip: '',
            tpTooltip: '',
            SLNumericLink: null,
            TPNumericLink: null,
            side: null,
            additionalClassName: '',
            overnightExecutionTextKey: 'panel.newOrderEntry.OvernightExecution',
            overnightExecutionTooltipKeyActive: 'panel.newOrderEntry.OvernightExecution.Info.ExistingOrders.Active',
            overnightExecutionTooltipKeyInActive: 'panel.newOrderEntry.OvernightExecution.Info.ExistingOrders.InActive',
            isAllowOTHTradingSLEvents: {
                onActiveChanged: (isActive) => {
                    this.set('dp.isAllowOTHTradingSL', isActive);
                }
            },
            isAllowOTHTradingTPEvents: {
                onActiveChanged: (isActive) => {
                    this.set('dp.isAllowOTHTradingTP', isActive);
                }
            }
        };
    },
    computed: {
        slValue: {
            get: function () {
                return this.get(this.get('dp.trailingStop.enabled') ? 'dp.trailingStop.value' : 'dp.sl.value');
            },
            set: function (value) {
                void this.set(this.get('dp.trailingStop.enabled') ? 'dp.trailingStop.value' : 'dp.sl.value', value);
            }
        },
        slMin: {
            get: function () {
                return this.get(this.get('dp.trailingStop.enabled') ? 'dp.trailingStop.minimalValue' : 'dp.sl.minimalValue');
            },
            set: function (value) {
                void this.set(this.get('dp.trailingStop.enabled')
                    ? 'dp.trailingStop.minimalValue'
                    : 'dp.sl.minimalValue', value);
            }
        },
        slMax: {
            get: function () {
                return this.get(this.get('dp.trailingStop.enabled') ? 'dp.trailingStop.maximalValue' : 'dp.sl.maximalValue');
            },
            set: function (value) {
                void this.set(this.get('dp.trailingStop.enabled')
                    ? 'dp.trailingStop.maximalValue'
                    : 'dp.sl.maximalValue', value);
            }
        },
        slStep: {
            get: function () {
                return this.get(this.get('dp.trailingStop.enabled') ? 'dp.trailingStop.increment' : 'dp.sl.increment');
            },
            set: function (value) {
                void this.set(this.get('dp.trailingStop.enabled') ? 'dp.trailingStop.increment' : 'dp.sl.increment', value);
            }
        },
        slPrecision: {
            get: function () {
                return this.get(this.get('dp.trailingStop.enabled')
                    ? 'dp.trailingStop.decimalPlaces'
                    : 'dp.sl.decimalPlaces');
            },
            set: function (value) {
                void this.set(this.get('dp.trailingStop.enabled')
                    ? 'dp.trailingStop.decimalPlaces'
                    : 'dp.sl.decimalPlaces', value);
            }
        },
        slCustomError: function () {
            const isTrailingStop = this.get('dp.trailingStop.enabled');
            const error = this.get(isTrailingStop ? 'dp.trailingStop.error' : 'dp.sl.error');
            return !isNullOrUndefined(error) ? error.toString() : '';
        },
        sllMin: function () {
            const inOffset = this.get('dp.sl.inOffset');
            const value = this.get(inOffset ? 'dp.sl.maximalValue' : 'dp.sl.minimalValue');
            return isNullOrUndefined(value) ? null : inOffset ? -value : value;
        },
        sllValue: {
            // #91413
            get: function () {
                let value = this.get('dp.sl.sllValue');
                if (isNaN(value) || value === null) {
                    value = this.get('dp.sl.value');
                    if (isNaN(value) || value === null) {
                        value = 0;
                    }
                }
                return value;
            },
            set: function (value) {
                if (this.get('dp.sllValueEnabled')) {
                    void this.set('dp.sl.sllValue', value);
                }
            }
        },
        sllMax: function () {
            return this.get('dp.sl.maximalValue');
        },
        sllPrecision: {
            get: function () {
                return this.get('dp.sl.decimalPlaces');
            },
            set: function (value) {
                if (this.get('dp.trailingStop.enabled')) {
                    return;
                }
                void this.set('dp.sl.decimalPlaces', value);
            }
        },
        sllStep: {
            get: function () {
                return this.get('dp.sl.increment');
            },
            set: function (value) {
                if (this.get('dp.trailingStop.enabled')) {
                    return;
                }
                void this.set('dp.sl.increment', value);
            }
        },
        sllVisible: function () {
            return !!(this.get('dp.sl.enabled') &&
                this.get('dp.sllValueEnabled') &&
                !this.get('dp.trailingStop.enabled'));
        },
        sltpTriggerShortValue: {
            // #109798
            get: function () {
                return this.get('dp.sltpTriggerShort');
            },
            set: function (value) {
                void this.set('dp.sltpTriggerShort', value);
            }
        }
    }
});
